import {
  DeesElement,
  html,
  property,
  customElement,
  cssManager,
  css,
  type CSSResult,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { themeDefaultStyles } from '../../00theme.js';

import { demoFunc } from './dees-icon.demo.js';

// Import Lucide icons and the createElement function
import * as lucideIcons from 'lucide';
import { createElement } from 'lucide';

/**
 * Convert a Lucide icon id to its PascalCase export name.
 * Lucide's canonical ids are kebab-case (e.g. "file-text", "message-square"),
 * but the package exports icons in PascalCase (e.g. "FileText", "MessageSquare").
 * Also passes through already-PascalCase and camelCase input unchanged-enough
 * (e.g. "Check" -> "Check", "eyeOff" -> "EyeOff").
 */
function lucideNameToPascalCase(name: string): string {
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

export type IconWithPrefix = `lucide:${string}` | string;

// Legacy type kept for consumers that still import it. `iconFA` itself is no longer supported.
export type TIconKey = string;

// Use a global static cache for all icons to reduce rendering
const iconCache = new Map<string, string>();

// Clear cache items occasionally to prevent memory leaks 
const MAX_CACHE_SIZE = 500;
function limitCacheSize() {
  if (iconCache.size > MAX_CACHE_SIZE) {
    // Remove oldest entries (first 20% of items)
    const keysToDelete = Array.from(iconCache.keys()).slice(0, MAX_CACHE_SIZE / 5);
    keysToDelete.forEach(key => iconCache.delete(key));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-icon': DeesIcon;
  }
}

@customElement('dees-icon')
export class DeesIcon extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Utility'];

  /**
   * @deprecated Use the `icon` property with format "lucide:iconName" instead.
   */
  @property({
    type: String,
    converter: {
      // Convert attribute string to property (for reflected attributes)
      fromAttribute: (value: string): TIconKey => value as TIconKey,
      // Convert property to attribute (for reflection)
      toAttribute: (value: TIconKey): string => value
    }
  })
  accessor iconFA: TIconKey | undefined = undefined;

  /**
   * The preferred icon property. Use format "lucide:iconName".
   * Example: "lucide:menu"
   */
  @property({
    type: String,
    converter: {
      fromAttribute: (value: string): IconWithPrefix => value as IconWithPrefix,
      toAttribute: (value: IconWithPrefix): string => value
    }
  })
  accessor icon: IconWithPrefix | undefined = undefined;

  @property({ type: Number })
  accessor iconSize!: number;

  @property({ type: String })
  accessor color: string = 'currentColor';

  @property({ type: Number })
  accessor strokeWidth: number = 2;

  // For tracking when we need to re-render
  private lastIcon: IconWithPrefix | TIconKey | null = null;
  private lastIconSize: number | null = null;
  private lastColor: string | null = null;
  private lastStrokeWidth: number | null = null;

  constructor() {
    super();
    domtools.elementBasic.setup();
  }

  /**
   * Gets the effective icon value. Legacy `iconFA` is intentionally rejected.
   */
  private getEffectiveIcon(): IconWithPrefix | TIconKey | null {
    // Prefer the new API
    if (this.icon) {
      return this.icon;
    }
    
    if (this.iconFA) {
      console.error(`dees-icon: iconFA is no longer supported. Use icon="lucide:${this.iconFA}" or another Lucide icon.`);
      return null;
    }
    
    return null;
  }
  
  /**
   * Parses an icon string into its type and name parts
   * @param iconStr The icon string in format "type:name"
   * @returns Object with type and name properties
   */
  private parseIconString(iconStr: string): { type: 'lucide' | 'unsupported', name: string } {
    const lowerStr = iconStr.toLowerCase();
    if (lowerStr.startsWith('fa:')) {
      return {
        type: 'unsupported',
        name: iconStr.substring(3) // Remove 'fa:' prefix
      };
    } else if (lowerStr.startsWith('lucide:')) {
      return {
        type: 'lucide',
        name: iconStr.substring(7) // Remove 'lucide:' prefix
      };
    } else {
      // Default to Lucide when no prefix is provided
      return {
        type: 'lucide',
        name: iconStr
      };
    }
  }

  private renderLucideIcon(iconName: string): string {
    // Create a cache key based on all visual properties
    const cacheKey = `lucide:${iconName}:${this.iconSize}:${this.color}:${this.strokeWidth}`;
    
    // Check if we already have this icon in the cache
    if (iconCache.has(cacheKey)) {
      return iconCache.get(cacheKey) || '';
    }

    try {
      // Get the Pascal case icon name (Menu instead of menu, FileText instead of file-text)
      const pascalCaseName = lucideNameToPascalCase(iconName);
      
      // Check if the icon exists in lucideIcons
      if (!(lucideIcons as any)[pascalCaseName]) {
        console.warn(`Lucide icon '${pascalCaseName}' not found in lucideIcons object`);
        return '';
      }

      // Use the exact pattern from Lucide documentation
      const svgElement = createElement((lucideIcons as any)[pascalCaseName], {
        color: this.color,
        size: this.iconSize,
        strokeWidth: this.strokeWidth
      });
      
      if (!svgElement) {
        console.warn(`createElement returned empty result for ${pascalCaseName}`);
        return '';
      }
      
      // Get the HTML
      const result = svgElement.outerHTML;
      
      // Cache the result for future use
      iconCache.set(cacheKey, result);
      limitCacheSize();
      
      return result;
    } catch (error) {
      console.error(`Error rendering Lucide icon ${iconName}:`, error);
      
      // Create a fallback SVG with the icon name
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${this.iconSize}" height="${this.iconSize}" viewBox="0 0 24 24" fill="none" stroke="${this.color}" stroke-width="${this.strokeWidth}" stroke-linecap="round" stroke-linejoin="round">
        <text x="50%" y="50%" font-size="6" text-anchor="middle" dominant-baseline="middle" fill="${this.color}">${iconName}</text>
      </svg>`;
    }
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        vertical-align: middle;
        pointer-events: none;
      }

      /* Improve rendering performance */
      #iconContainer svg {
        display: block;
        height: 100%;
        width: 100%;
        will-change: transform; /* Helps with animations */
        contain: strict; /* Performance optimization */
      }
    `,
  ];

  public render() {
    return html`
      ${domtools.elementBasic.styles}
      <style>
        #iconContainer {
          width: ${this.iconSize}px;
          height: ${this.iconSize}px;
        }
      </style>
      <div id="iconContainer"></div>
    `;
  }

  public updated() {
    // If size is not specified, use font size as a base
    if (!this.iconSize) {
      this.iconSize = parseInt(globalThis.getComputedStyle(this).fontSize.replace(/\D/g,''));
    }
    
    const effectiveIcon = this.getEffectiveIcon();
    const container = this.shadowRoot?.querySelector('#iconContainer');
    if (!container) return;

    if (!effectiveIcon) {
      container.innerHTML = '';
      this.lastIcon = effectiveIcon;
      this.lastIconSize = this.iconSize;
      this.lastColor = this.color;
      this.lastStrokeWidth = this.strokeWidth;
      return;
    }
    
    // Check if we actually need to update the icon
    // This prevents unnecessary DOM operations when properties haven't changed
    if (this.lastIcon === effectiveIcon && 
        this.lastIconSize === this.iconSize && 
        this.lastColor === this.color && 
        this.lastStrokeWidth === this.strokeWidth) {
      return; // No visual changes - skip update
    }
    
    // Update our "last properties" for future change detection
    this.lastIcon = effectiveIcon;
    this.lastIconSize = this.iconSize;
    this.lastColor = this.color;
    this.lastStrokeWidth = this.strokeWidth;
    
    try {
      // Parse the icon string to get type and name
      const { type, name } = this.parseIconString(effectiveIcon);
      
      if (type === 'lucide') {
        // For Lucide, use direct DOM manipulation as shown in the docs
        // This approach avoids HTML string issues
        container.innerHTML = ''; // Clear container
        
        try {
          // Convert to PascalCase (handles kebab-case Lucide ids like "file-text")
          const pascalCaseName = lucideNameToPascalCase(name);
          
          if ((lucideIcons as any)[pascalCaseName]) {
            // Use the documented pattern from Lucide docs
            const svgElement = createElement((lucideIcons as any)[pascalCaseName], {
              color: this.color,
              size: this.iconSize,
              strokeWidth: this.strokeWidth
            });
            
            if (svgElement) {
              // Directly append the element
              container.appendChild(svgElement);
              return; // Exit early since we've added the element
            }
          }
          
          // If we reach here, something went wrong
          throw new Error(`Could not create element for ${pascalCaseName}`);
        } catch (error) {
          console.error(`Error rendering Lucide icon:`, error);
          
          // Fall back to the string-based approach
          const iconHtml = this.renderLucideIcon(name);
          if (iconHtml) {
            container.innerHTML = iconHtml;
          }
        }
      } else {
        container.innerHTML = '';
        console.error(`dees-icon: fa:${name} is no longer supported. Use icon="lucide:${name}" or another Lucide icon.`);
      }
    } catch (error) {
      console.error(`Error updating icon ${effectiveIcon}:`, error);
    }
  }

  // Clean up resources when element is removed
  async disconnectedCallback() {
    super.disconnectedCallback();
    
    // Clear our references
    this.lastIcon = null;
    this.lastIconSize = null;
    this.lastColor = null;
    this.lastStrokeWidth = null;
  }
}
